/**
 * Sign In Page
 *
 * Authentication page with Auth0 sign-in and sign-up options.
 * Redirects to Auth0 for authentication.
 *
 * Styled on the CurAItion editorial design system (Journey C — cream/olive),
 * shared with the landing page: cream page wash with a masked grid texture,
 * Libre Caslon serif wordmark, a mono eyebrow, and olive primary actions.
 * All colors come from design tokens (globals.css → tailwind.config.js); no
 * hardcoded hex lives here.
 */

import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface SignInLocationState {
  from?: string;
}

const AUTH0_DB_CONNECTION = 'Username-Password-Authentication';

export function SignInPage() {
  const { loginWithRedirect, isLoading, error } = useAuth0();
  const location = useLocation();

  // Password reset, inline (CUR-1349). Deliberately self-contained rather than
  // a hop to Auth0: this page previously offered NO recovery route at all, and
  // the extra hop is exactly the one a locked-out user does not take. A client
  // admin sat locked out for 18 days and 4 hours because of that; when he
  // finally reached this flow it took him 103 seconds.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetState, setResetState] = useState<'idle' | 'sending' | 'sent'>('idle');

  // Deep-link survival across the Auth0 round-trip: if ProtectedRoute
  // bounced us here, `state.from` carries the original path. Forward it
  // to Auth0 via `appState.returnTo` so the post-callback hop lands
  // back on the deep-linked URL rather than `/dashboard`.
  const returnTo = (location.state as SignInLocationState | null)?.from;

  const handleSignIn = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'login',
      },
      appState: returnTo ? { returnTo } : undefined,
    });
  };

  const handleSignUp = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      },
      appState: returnTo ? { returnTo } : undefined,
    });
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail || resetState === 'sending') return;
    setResetState('sending');

    try {
      await fetch(`https://${import.meta.env.VITE_AUTH0_DOMAIN}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
          email: resetEmail,
          connection: AUTH0_DB_CONNECTION,
        }),
      });
    } catch {
      // Swallowed on purpose — see the unconditional 'sent' below.
    }

    // Always report the same outcome, success or failure. Branching here would
    // turn this box into an account-existence oracle for anyone who wants to
    // enumerate our client list. Auth0's own endpoint is deliberately opaque
    // for the same reason; a user who typed the wrong address simply gets no
    // email, which is the correct and expected result.
    setResetState('sent');
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      {/* Atmospheric grid texture — mirrors the landing-page hero, masked to
          fade toward the edges so it reads as depth, not chrome. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--foreground) / 0.04) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--foreground) / 0.04) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage:
            'radial-gradient(ellipse 70% 60% at 50% 30%, #000 20%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 70% 60% at 50% 30%, #000 20%, transparent 80%)',
        }}
      />

      <div className="relative w-full max-w-[420px]">
        {/* Brand */}
        <div className="mb-8 text-center">
          <h1 className="font-serif text-5xl font-medium tracking-tight text-foreground">
            CurAItion
          </h1>
          <p className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-olive">
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 rounded-full bg-olive shadow-[0_0_0_4px_hsl(var(--olive)/0.14)]"
            />
            AI-Powered Cultural Intelligence
          </p>
        </div>

        {/* Sign in card */}
        <div className="rounded-xl border border-border bg-card p-8 shadow-sm sm:p-10">
          <div className="mb-8 text-center">
            <h2 className="font-serif text-2xl text-foreground">Welcome</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign in to access your content dashboard
            </p>
          </div>

          {/* Error display */}
          {error && (
            <div
              role="alert"
              className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-center text-sm text-destructive"
            >
              {error.message || 'Authentication failed. Please try again.'}
            </div>
          )}

          {/* Auth actions */}
          <div className="flex flex-col gap-4">
            <Button
              type="button"
              size="lg"
              onClick={handleSignIn}
              disabled={isLoading}
              className="h-11 w-full text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Connecting…
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-[0.14em]">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={handleSignUp}
              disabled={isLoading}
              className="h-11 w-full text-base"
            >
              Create Account
            </Button>
          </div>

          {/* CUR-1349. This slot used to signpost the CUR-1062 reconnect path:
              "Returning from before our July upgrade? Choose Create Account with
              your existing email." That advice is true exactly ONCE — while a
              user's `auth0_sub` is still NULL. The moment they reconnect, the sub
              is linked and a second signup raises `account_conflict`, which has
              no self-heal and locks them out permanently. The copy was
              unconditional, so it kept advising users for whom it had become the
              single worst available action. A Gymshark admin followed it twice:
              it worked in July, then dead-ended him on "The user already exists."

              The remaining stranded users are known by name and are contacted
              directly under CUR-1080, so the signpost has no audience left worth
              the trap. Replaced with the recovery route this page never had. */}
          {resetState === 'sent' ? (
            <p className="mt-6 text-center text-xs text-text-tertiary">
              If an account exists for that address, a reset link is on its way. Check your inbox,
              then choose <span className="font-medium text-foreground">Sign In</span>.
            </p>
          ) : resetOpen ? (
            <form onSubmit={handleResetSubmit} className="mt-6 flex flex-col gap-2">
              <label htmlFor="reset-email" className="text-center text-xs text-text-tertiary">
                Enter your email and we'll send a reset link.
              </label>
              <div className="flex gap-2">
                <input
                  id="reset-email"
                  type="email"
                  required
                  autoFocus
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                />
                <Button type="submit" variant="outline" size="sm" disabled={resetState === 'sending'}>
                  {resetState === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
                </Button>
              </div>
            </form>
          ) : (
            <p className="mt-6 text-center text-xs text-text-tertiary">
              Forgotten your password?{' '}
              <button
                type="button"
                onClick={() => setResetOpen(true)}
                className="font-medium text-foreground underline underline-offset-2"
              >
                Reset it
              </button>
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-text-tertiary">
          Secure authentication powered by Auth0
        </p>
      </div>
    </div>
  );
}
